import { Component, OnInit } from '@angular/core';
import { CommonModule, DecimalPipe } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatDialogModule, MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { ToolsService } from '../../services/tools.service';
import {
  SummaryReportDto,
  MixReportDto,
  RheologyReportDto,
  RheologyTypes,
  BobAndSleeveTypes,
  CompStrengthTypes,
  StaticGelStrengthTypes,
  HpHtRheologyTypes,
  FluidLossTypes,
  ThickeningTimeReportDto
} from '../../services/summary-report.service';
import { PrintService } from '../../shared/print/print-dialog.component';

@Component({
  selector: 'app-summary-report-by-date',
  imports: [CommonModule, FormsModule, DecimalPipe, MatDialogModule, MatButtonModule, MatIconModule],
  templateUrl: './summary-report-by-date.component.html',
  styleUrl: './summary-report-by-date.component.css',
})
export class SummaryReportByDateComponent implements OnInit {

  fromDate = '';
  fromTime = '0:00';
  toDate = '';
  toTime = '23:59';

  allReports: SummaryReportDto[] = [];
  filteredReports: SummaryReportDto[] = [];
  projectNames: string[] = [];
  selectedProjectName: string | null = null;

  isLoadingReports = false;
  errorMessage = '';
  isMaximized = false;

  constructor(
    private toolsService: ToolsService,
    private printService: PrintService,
    public dialogRef: MatDialogRef<SummaryReportByDateComponent>
  ) { }

  ngOnInit(): void {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    this.fromDate = this.toIsoDate(yesterday);
    this.toDate = this.toIsoDate(today);
  }

  private toIsoDate(d: Date): string {
    return d.toISOString().substring(0, 10);
  }

  refresh(): void {
    if (!this.fromDate || !this.toDate) {
      this.errorMessage = 'Please select both From and To dates.';
      return;
    }

    const fromDateTime = this.combineDateAndTime(this.fromDate, this.fromTime);
    const toDateTime = this.combineDateAndTime(this.toDate, this.toTime);

    this.isLoadingReports = true;
    this.errorMessage = '';
    this.allReports = [];
    this.filteredReports = [];
    this.projectNames = [];
    this.selectedProjectName = null;

    this.toolsService.getSummaryReportByDate(fromDateTime, toDateTime).subscribe({
      next: (reports) => {
        this.allReports = reports;
        this.projectNames = [...new Set(reports.map(r => r.name))].sort();
        this.filteredReports = [...this.allReports];
        this.isLoadingReports = false;
      },
      error: (err) => {
        console.error('Error loading summary reports:', err);
        this.errorMessage = 'Failed to load summary reports. Please try again.';
        this.isLoadingReports = false;
      }
    });
  }

  private combineDateAndTime(dateStr: string, timeStr: string): string {
    const [hours, minutes] = (timeStr || '0:00').split(':').map(s => parseInt(s, 10) || 0);
    // Parse date as local time and set hours/minutes
    const [year, month, day] = dateStr.split('-').map(s => parseInt(s, 10));
    const d = new Date(year, month - 1, day, hours, minutes, 0, 0);

    // Format as ISO string but keep it as local time for API
    // The API will receive this as DateTime parameter
    const pad = (n: number) => n.toString().padStart(2, '0');
    return `${year}-${pad(month)}-${pad(day)}T${pad(hours)}:${pad(minutes)}:00`;
  }

  onProjectChange(): void {
    if (this.selectedProjectName === null) {
      // "All" selected
      this.filteredReports = [...this.allReports];
    } else {
      this.filteredReports = this.allReports.filter(r => r.name === this.selectedProjectName);
    }
  }

  // Helper methods for template

  /**
     * Get rheology type display name
     */
  getRheologyTypeName(type: RheologyTypes): string {
    switch (type) {
      case RheologyTypes.Surface:
        return 'Surface';
      case RheologyTypes.Downhole:
        return 'Downhole';
      default:
        return 'Unknown';
    }
  }

  getSurfaceRheology(mix: MixReportDto): RheologyReportDto[] {
    if (!mix.rheologyReports) return [];
    return mix.rheologyReports.filter(r => r.type === RheologyTypes.Surface);
  }

  getDownholeRheology(mix: MixReportDto): RheologyReportDto[] {
    if (!mix.rheologyReports) return [];
    return mix.rheologyReports.filter(r => r.type === RheologyTypes.Downhole);
  }

  getBobAndSleeveTypeName(type: BobAndSleeveTypes): string {
    switch (type) {
      case BobAndSleeveTypes.R1B1: return 'R1B1';
      case BobAndSleeveTypes.R1B5: return 'R1B5';
      case BobAndSleeveTypes.Fysa: return 'FYSA';
      default: return '';
    }
  }

  getCompStrengthTypeName(type: CompStrengthTypes): string {
    switch (type) {
      case CompStrengthTypes.Uca: return 'UCA';
      case CompStrengthTypes.Crush: return 'Crush';
      case CompStrengthTypes.ConditionedUCA: return 'Conditioned UCA';
      case CompStrengthTypes.FoamUca: return 'Foam UCA';
      case CompStrengthTypes.FoamCrush: return 'Foam Crush';
      case CompStrengthTypes.FoamConditionedUCA: return 'Foam Conditioned UCA';
      default: return '';
    }
  }

  getFluidLossTypeName(type: FluidLossTypes): string {
    switch (type) {
      case FluidLossTypes.Static: return 'Static';
      case FluidLossTypes.Stirred: return 'Stirred';
      default: return '';
    }
  }

  getSortedFluidLossReports(reports: any[]) {
    return [...reports].sort((a, b) => a.type - b.type);
  }

  getScheduleText(report: ThickeningTimeReportDto | null | undefined): string {
    if (!report) return '';

    const initTemp = report.initialTemperature || 0;
    const initPressure = report.initialPressure || 0;
    const finalTemp = report.finalTemperature || 0;
    const finalPressure = report.finalPressure || 0;
    const tempRampTime = report.temperatureRampTime || 0;
    const pressureRampTime = report.pressureRampTime || 0;
    const stopMotor = report.stopMotor || 0;
    const startMotor = report.startMotor || 0;
    const conditioningTime = report.conditioningTime || 0;
    const gradient = report.gradient || 0;
    const bhst = report.bhst || 0;
    const isHesitation = report.isHesitation || false;
    const isShutdown = report.isShutdown || false;

    let schedule = `Start at ${initTemp}°F and ${initPressure} psi. `;
    schedule += `Ramp temperature to ${finalTemp}°F in ${tempRampTime} min. `;
    schedule += `Ramp pressure to ${finalPressure} psi in ${pressureRampTime} min. `;

    if (isHesitation && isShutdown) {
      // HesitationShutdownSchedule
      schedule += `Hold pressure for duration. `;
      schedule += `Shutdown motor after ${stopMotor} min for ${startMotor - stopMotor} min then start motor back up at ${startMotor} min. `;
      schedule += `Hold temperature for ${conditioningTime} min then begin hesitation cycles while increasing temperature at ${gradient.toFixed(2)}°F/min until reaching ${bhst}°F.`;
    } else if (isHesitation) {
      // HesitationSchedule
      schedule += `Hold pressure for duration. `;
      schedule += `Hold temperature for ${conditioningTime} min then begin hesitation cycles while increasing temperature at ${gradient.toFixed(2)}°F/min until reaching ${bhst}°F.`;
    } else if (isShutdown) {
      // ShutdownSchedule
      schedule += `Hold temperature and pressure for duration. `;
      schedule += `Shutdown motor after ${stopMotor} min for ${startMotor - stopMotor} min then start motor back up at ${startMotor} min. Then run for duration.`;
    } else {
      // GeneralSchedule
      schedule += `Hold temperature and pressure for duration of the test.`;
    }

    return schedule;
  }

  formatTime(minutes: number): string {
    if (!minutes) return '';
    const hrs = Math.floor(minutes / 60);
    const mins = Math.floor(minutes % 60);
    return `${hrs}:${mins.toString().padStart(2, '0')}`;
  }

  getHphtTypeName(type: HpHtRheologyTypes | number | undefined): string {
    switch (type) {
      case HpHtRheologyTypes.Fan77:
        return 'Fann 77';
      case HpHtRheologyTypes.Chann7600:
        return 'Chann 7600';
      case HpHtRheologyTypes.Chann7550:
        return 'Chann 7550';
      default:
        return 'Unknown';
    }
  }

  getStaticGelTypeName(type: StaticGelStrengthTypes | number | undefined): string {
    switch (type) {
      case StaticGelStrengthTypes.SGSA:
        return 'SGSA';
      case StaticGelStrengthTypes.MACS:
        return 'MACS';
      default:
        return 'Unknown';
    }
  }

  getConfCSTypeName(type: number): string {
    switch (type) {
      case 0:
        return 'Tangent';
      case 1:
        return 'Average';
      case 2:
        return 'Secant';
      default:
        return '';
    }
  }

  /**
   * Get fluid mixture text for spacer compatibility
   * Translates spacer percentage and ratio types into readable text
   */
  getFluidMixtureText(spacerPercent: number, isSlurryBased: boolean | null, ratioType1: number | null, ratioType2: number | null): string {
    // Mud (0) + Cement (1)
    if (ratioType1 === 0 && ratioType2 === 1) {
      if (spacerPercent === 100) return '100% Cement';
      if (spacerPercent === 0) return '100% Mud';
      return `${100 - spacerPercent}% Mud ${spacerPercent}% Cement`;
    }
    // Mud (0) + Spacer (2)
    if (ratioType1 === 0 && ratioType2 === 2) {
      if (spacerPercent === 100) return '100% Spacer';
      if (spacerPercent === 0) return '100% Mud';
      return `${100 - spacerPercent}% Mud ${spacerPercent}% Spacer`;
    }
    // Cement (1) + Spacer (2)
    if (ratioType1 === 1 && ratioType2 === 2) {
      if (spacerPercent === 100) return '100% Spacer';
      if (spacerPercent === 0) return '100% Cement';
      return `${100 - spacerPercent}% Cement ${spacerPercent}% Spacer`;
    }
    // Cement (1) + Mud (0)
    if (ratioType1 === 1 && ratioType2 === 0) {
      if (spacerPercent === 100) return '100% Mud';
      if (spacerPercent === 0) return '100% Cement';
      return `${100 - spacerPercent}% Cement ${spacerPercent}% Mud`;
    }
    // Spacer (2) + Mud (0)
    if (ratioType1 === 2 && ratioType2 === 0) {
      if (spacerPercent === 100) return '100% Mud';
      if (spacerPercent === 0) return '100% Spacer';
      return `${100 - spacerPercent}% Spacer ${spacerPercent}% Mud`;
    }
    // Spacer (2) + Cement (1)
    if (ratioType1 === 2 && ratioType2 === 1) {
      if (spacerPercent === 100) return '100% Cement';
      if (spacerPercent === 0) return '100% Spacer';
      return `${100 - spacerPercent}% Spacer ${spacerPercent}% Cement`;
    }
    return '';
  }

  /**
   * Check if data should be highlighted based on lastRecordDate being within the selected date range
   * Matches the C# logic: IsRecentOutPut = !((_fromDate == null) || (!om.LastRecordDate.HasValue)) && (om.LastRecordDate >= _fromDate && om.LastRecordDate <= _toDate)
   */
  isRecentOutput(lastRecordDate: Date | string | null | undefined): boolean {
    if (!lastRecordDate || !this.fromDate || !this.toDate) {
      return false;
    }

    // Parse the lastRecordDate to a Date object if it's a string
    const recordDate = typeof lastRecordDate === 'string' ? new Date(lastRecordDate) : lastRecordDate;

    // Parse the from and to dates with their times
    const fromDateTime = new Date(this.combineDateAndTime(this.fromDate, this.fromTime));
    const toDateTime = new Date(this.combineDateAndTime(this.toDate, this.toTime));

    // Check if recordDate is within the range
    return recordDate >= fromDateTime && recordDate <= toDateTime;
  }

  /**
   * Print the summary report by date
   */
  printReport(): void {
    const contentEl = document.querySelector<HTMLElement>('.report-region');
    this.printService.print(contentEl, {
      colorMap: {
        'bg-yellow': { bg: '#FAAB18' },
        'bg-green': { bg: '#b2cc34' },
        'bg-cyan': { bg: '#00b2bd' },
        'bg-orange': { bg: '#e5601f' },
        'highlight-yellow': { bg: '#ffff00' },
      },
      decorators: [
        {
          selector: 'tr.bg-red > th, tr.bg-red > td',
          style: 'background-color: #e21836 !important; color: #000 !important; text-align: center !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;',
        },
        {
          selector: 'tr.bg-gray > th, tr.bg-gray > td',
          style: 'background-color: #ba3093 !important; color: #fff !important; text-align: center !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;',
        },
        {
          selector: 'tr.bg-purple > th, tr.bg-purple > td',
          style: 'background-color: #751269 !important; color: #fff !important; text-align: center !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;',
        },
        {
          selector: 'tr.bg-blue > th, tr.bg-blue > td',
          style: 'background-color: #009DD9 !important; color: #fff !important; text-align: center !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;',
        },
        {
          selector: '.custom-mix-table, .mix-header-table',
          style: 'background-color: #fff !important; border: 1px solid #000 !important; border-collapse: collapse !important; page-break-inside: avoid !important; break-inside: avoid !important; width: 100% !important; table-layout: auto !important; font-size: 11px !important;',
        },
        {
          selector: '.project-info-header tr, .project-info-header td, .project-info-header div',
          style: 'background-color: #fff !important;',
        },
        {
          selector: '.custom-mix-table:not(.project-info-header) tr, .custom-mix-table:not(.project-info-header) td, .mix-header-table:not(.project-info-header) tr, .mix-header-table:not(.project-info-header) td',
          style: 'background-color: #FAAB18 !important; border: none !important; padding: 2px 6px !important; vertical-align: middle; line-height: 1.3; word-wrap: break-word !important; overflow-wrap: break-word !important; white-space: normal !important;',
        },
        {
          selector: 'tr.bg-orange > th',
          style: 'background-color: #E5601F !important; border: 1px solid #000 !important; padding: 2px 6px !important; vertical-align: middle; line-height: 1.3;',
        },
        {
          selector: 'table.table-bordered, table.table-bordered th, table.table-bordered td',
          style: `border: 1px solid #000 !important; border-collapse: collapse !important;`,
        },
        {
          selector: '.mixability-row td',
          style: 'border-bottom: 1px solid #000 !important;',
        },
        {
          selector: '.test-table, .rheology-table',
          style: 'outline: 1px solid #000 !important; outline-offset: -1px !important; border-collapse: collapse !important; page-break-inside: avoid !important; break-inside: avoid !important; width: 100% !important; table-layout: auto !important;',
        },
        {
          selector: '.test-table th, .test-table td, .rheology-table th, .rheology-table td',
          style: 'border: 1px solid #000 !important; word-wrap: break-word !important; overflow-wrap: break-word !important;',
        },
        {
          selector: '.notes-section',
          style: 'border: 1px solid #000 !important; page-break-inside: avoid !important; break-inside: avoid !important; word-wrap: break-word !important; overflow-wrap: break-word !important;',
        },
        { selector: '.white-text', style: 'color: #fff !important;' },
        { selector: '.center', style: 'text-align: center !important;' },
        { 
          selector: '.inline-item', 
          style: 'display: inline; margin-right: 12px; white-space: normal !important; word-wrap: break-word !important; overflow-wrap: break-word !important;' 
        },
        {
          selector: '.custom-mix-table td, .mix-header-table td, .test-table td, .test-table th, .rheology-table td, .rheology-table th',
          style: 'color: #000 !important; font-weight: bold !important; word-wrap: break-word !important; overflow-wrap: break-word !important; max-width: 100% !important;',
        },
        {
          selector: '.highlight-yellow',
          style: 'background-color: #ffff00 !important; -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important;',
        },
        {
          selector: '.report-content-wrapper',
          style: 'max-width: 100% !important; overflow: hidden !important;',
        },
        {
          selector: '.mix-section',
          style: 'max-width: 100% !important; overflow: hidden !important;',
        },
        {
          selector: '.summary-content, .container-fluid',
          style: 'max-width: 100% !important; overflow: hidden !important; box-sizing: border-box !important;',
        },
        {
          selector: 'body',
          style: 'margin: 0.5in !important; padding: 0 !important;',
        },
      ],
      fallbackBodyClass: 'summary-report-by-date-printing',
    });
  }

  toggleMaximize(): void {
    if (!this.isMaximized) {
      this.dialogRef.addPanelClass('summary-report-maximized');
      this.isMaximized = true;
    } else {
      this.dialogRef.removePanelClass('summary-report-maximized');
      this.isMaximized = false;
    }
  }

  /**
   * Close the dialog
   */
  close(): void {
    this.dialogRef.close();
  }
}

